import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, FormSection, ToggleField } from "@/components/shared/form";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { INDIAN_STATES } from "@/features/clients/constants";
import {
  useCreateVendor,
  useNextVendorCode,
  useUpdateVendor,
  useVendor,
  vendorToForm,
} from "@/features/vendors/api";
import { GST_TREATMENTS, PAYMENT_TERMS, VENDOR_TYPES } from "@/features/vendors/constants";
import {
  emptyVendorForm,
  vendorFormSchema,
  type VendorFormValues,
} from "@/features/vendors/schema";

export function VendorNewPage() {
  return (
    <ErrorBoundary title="Vendor form crashed">
      <VendorFormPage mode="create" />
    </ErrorBoundary>
  );
}

export function VendorEditPage({ vendorId }: { vendorId: string }) {
  return (
    <ErrorBoundary title="Vendor form crashed">
      <VendorFormPage mode="edit" vendorId={vendorId} />
    </ErrorBoundary>
  );
}

function VendorFormPage({ mode, vendorId }: { mode: "create" | "edit"; vendorId?: string }) {
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const { data: vendor, isLoading } = useVendor(isEdit ? vendorId : undefined);
  const { data: nextCode } = useNextVendorCode(!isEdit);
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();

  const form = useForm<VendorFormValues>({
    resolver: zodResolver(vendorFormSchema),
    defaultValues: emptyVendorForm,
    mode: "onBlur",
  });

  const { errors, isDirty } = form.formState;

  useEffect(() => {
    if (isEdit && vendor) form.reset(vendorToForm(vendor));
  }, [isEdit, vendor, form]);

  useEffect(() => {
    if (!isEdit && nextCode && !form.getValues("code")) {
      form.setValue("code", nextCode);
    }
  }, [isEdit, nextCode, form]);

  const submit = form.handleSubmit(async (values) => {
    if (isEdit && vendorId) {
      await updateVendor.mutateAsync({ id: vendorId, values });
      void navigate({ to: "/vendors/$vendorId/profile", params: { vendorId } });
      return;
    }
    const created = await createVendor.mutateAsync(values);
    void navigate({ to: "/vendors/$vendorId/profile", params: { vendorId: created.id } });
  });

  const saving = createVendor.isPending || updateVendor.isPending;

  if (isEdit && isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading vendor…</p>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-3 pb-20">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon-sm" aria-label="Back">
            {isEdit && vendorId ? (
              <Link to="/vendors/$vendorId/profile" params={{ vendorId }}>
                <ArrowLeft />
              </Link>
            ) : (
              <Link to="/vendors">
                <ArrowLeft />
              </Link>
            )}
          </Button>
          <div>
            <h1 className="text-lg font-semibold tracking-[-0.01em]">
              {isEdit ? "Edit vendor" : "New vendor"}
            </h1>
            <p className="text-xs text-muted-foreground">Supplier master for purchase orders</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button asChild type="button" variant="outline" size="sm">
            <Link to="/vendors">Cancel</Link>
          </Button>
          <Button type="button" size="sm" disabled={saving || (isEdit && !isDirty)} onClick={() => void submit()}>
            {saving ? <Loader2 className="animate-spin" /> : null}
            Save
          </Button>
        </div>
      </header>

      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormSection title="Identity">
          <FieldGrid>
            <Field label="Name" required span={8} error={errors.name?.message} htmlFor="v-name">
              <Input id="v-name" autoFocus {...form.register("name")} />
            </Field>
            <Field label="Code" required span={4} error={errors.code?.message} htmlFor="v-code">
              <Input id="v-code" className="uppercase" {...form.register("code")} />
            </Field>
            <Field label="Type" span={4} htmlFor="v-type">
              <NativeSelect id="v-type" {...form.register("vendor_type")}>
                {VENDOR_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field
              label="Batch prefix"
              required
              span={4}
              error={errors.batch_prefix?.message}
              hint="Used on GRN batch numbers"
              htmlFor="v-pfx"
            >
              <Input id="v-pfx" className="uppercase" {...form.register("batch_prefix")} />
            </Field>
            <ToggleField
              label="Active"
              checked={form.watch("is_active")}
              onChange={(v) => form.setValue("is_active", v, { shouldDirty: true })}
              span={4}
            />
          </FieldGrid>
        </FormSection>

        <FormSection title="Contact">
          <FieldGrid>
            <Field label="Phone" span={4} htmlFor="v-phone">
              <Input id="v-phone" {...form.register("work_phone")} />
            </Field>
            <Field label="Email" span={4} error={errors.email?.message} htmlFor="v-email">
              <Input id="v-email" {...form.register("email")} />
            </Field>
            <Field label="Website" span={4} htmlFor="v-web">
              <Input id="v-web" {...form.register("website")} />
            </Field>
          </FieldGrid>
        </FormSection>

        <FormSection title="Address">
          <FieldGrid>
            <Field label="Address line 1" required span={12} error={errors.addr1?.message} htmlFor="v-a1">
              <Input id="v-a1" {...form.register("addr1")} />
            </Field>
            <Field label="Address line 2" span={12} htmlFor="v-a2">
              <Input id="v-a2" {...form.register("addr2")} />
            </Field>
            <Field label="City" required span={4} error={errors.city?.message} htmlFor="v-city">
              <Input id="v-city" {...form.register("city")} />
            </Field>
            <Field label="State" required span={4} error={errors.state?.message} htmlFor="v-state">
              <NativeSelect id="v-state" {...form.register("state")}>
                <option value="">Select…</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s.code} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="PIN" required span={4} error={errors.pincode?.message} htmlFor="v-pin">
              <Input id="v-pin" {...form.register("pincode")} />
            </Field>
            <Field label="Place of supply" span={6} hint="Defaults to state for GST" htmlFor="v-pos">
              <Input id="v-pos" {...form.register("place_of_supply")} />
            </Field>
          </FieldGrid>
        </FormSection>

        <FormSection title="Statutory">
          <FieldGrid>
            <Field label="GST treatment" span={4} htmlFor="v-gstt">
              <NativeSelect id="v-gstt" {...form.register("gst_treatment")}>
                {GST_TREATMENTS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="GSTIN" span={4} htmlFor="v-gstin">
              <Input id="v-gstin" className="uppercase" maxLength={15} {...form.register("gstin")} />
            </Field>
            <Field label="PAN" span={4} htmlFor="v-pan">
              <Input id="v-pan" className="uppercase" maxLength={10} {...form.register("pan_no")} />
            </Field>
          </FieldGrid>
        </FormSection>

        <FormSection title="Commercial">
          <FieldGrid>
            <Field label="Payment terms" span={4} htmlFor="v-terms">
              <NativeSelect id="v-terms" {...form.register("payment_terms")}>
                {PAYMENT_TERMS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Credit days" span={4} htmlFor="v-days">
              <Input id="v-days" type="number" {...form.register("credit_days")} />
            </Field>
            <Field label="Credit limit" span={4} htmlFor="v-limit">
              <Input id="v-limit" type="number" step="1000" {...form.register("credit_limit")} />
            </Field>
            <Field
              label="Opening balance"
              span={4}
              error={errors.opening_balance?.message}
              hint="Brought forward AP so ledgers match"
              htmlFor="v-ob"
            >
              <Input id="v-ob" type="number" step="0.01" {...form.register("opening_balance")} />
            </Field>
            <Field
              label="As of"
              span={4}
              error={errors.opening_balance_as_of?.message}
              hint="Cut-off date for opening balance"
              htmlFor="v-ob-date"
            >
              <Input id="v-ob-date" type="date" {...form.register("opening_balance_as_of")} />
            </Field>
            <Field label="Notes" span={12} htmlFor="v-notes">
              <Textarea id="v-notes" rows={3} {...form.register("notes")} />
            </Field>
          </FieldGrid>
        </FormSection>
      </form>
    </div>
  );
}
